import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import WhatsAppButton from '../components/WhatsAppButton';
import EmailButton from '../components/EmailButton';
import { faqPageSchema } from '../lib/schema';
import { customerReviews } from '../data/reviewsData';

// Commercial painting: offices, shops, clinics and rental properties across Kent.
// Only state what is true of the business. Reviews are real Checkatrade reviews
// from commercial and landlord customers (ids below).
const PATH = '/commercial-painting-kent';
const REVIEW_IDS = [31, 27];

const WHO = [
  {
    name: 'Offices',
    text: 'Small offices repainted around your working day. We can work in phases, room by room, so staff keep working, and keep the space tidy at the end of each day.',
  },
  {
    name: 'Shops and clinics',
    text: "Independent shops, salons and clinics, scheduled around trading hours. Where it helps, we work out of hours so you don't have to close.",
  },
  {
    name: 'Schools',
    text: 'Classrooms, halls, corridors and staff areas, planned around term dates so the work happens while pupils are away.',
  },
  {
    name: 'Landlords and managing agents',
    text: 'Refreshes between tenants, planned around your letting dates, so the property is ready for the next move-in.',
  },
];

const HOW = [
  ['A visit and a written quote', 'We come and look at the premises, agree what needs doing and when, then send a clear written quote.'],
  ['A plan that keeps you open', 'We agree which areas we do when: in phases, or out of hours where needed.'],
  ['Proper preparation', 'Filling, sanding, priming and repairs before any paint goes on, so the finish lasts in a busy space.'],
  ['Tidy every day', 'Work areas are cleared and left safe at the end of each day, so your staff and customers can use the space.'],
];

const FAQS = [
  {
    question: 'Can you repaint an office or shop without us closing?',
    answer:
      'Usually, yes. We plan the work around your opening hours, in phases or out of hours where needed, and leave each area tidy at the end of the day.',
  },
  {
    question: 'Which areas do you cover for commercial work?',
    answer:
      'We are based in Wye, near Ashford, and take on commercial jobs across Kent, including Ashford, Canterbury and Maidstone.',
  },
  {
    question: 'Do you paint schools?',
    answer:
      'Yes. We plan school work around term dates, so classrooms, halls and corridors are painted while pupils are away, and agree a schedule with the school before we start.',
  },
  {
    question: 'Do you work for landlords and managing agents?',
    answer:
      'Yes. We do refreshes between tenants, planned around letting dates, for individual landlords and agents.',
  },
  {
    question: 'Do you offer free quotes for commercial work?',
    answer:
      'Yes. We visit, talk through what you need and when, then send a clear written quote with no obligation.',
  },
  {
    question: 'Are you insured?',
    answer: 'Yes. We carry appropriate insurance for the work we do.',
  },
];

const CommercialPainting = () => {
  const reviews = customerReviews.filter((r) => REVIEW_IDS.includes(r.id));

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Commercial Painters in Kent: Offices, Shops & Rentals | Paint It Better"
        description="Commercial painting and decorating in Kent for offices, shops, clinics, schools and rental properties. Work planned around your opening hours, in phases or out of hours. Free quotes."
        canonical={PATH}
      />
      <JsonLd data={faqPageSchema(FAQS, PATH)} />

      <section className="relative pt-36 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-cyan-300 font-medium mb-4">Commercial painting, Kent</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Commercial painters for offices, shops, schools and rentals in Kent
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Romeo and the team repaint offices, shops, clinics, schools and rental properties across Kent, from our base in
              Wye near Ashford. We plan the work around your business, in phases or out of hours where needed, so you
              can stay open while we work. For schools, we work around term dates.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6 h-9">Free Quote</Button>
              </Link>
              <WhatsAppButton />
              <EmailButton />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Who we paint for</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHO.map((s) => (
              <div key={s.name} className="rounded-2xl border border-slate-200 p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-2">
                  <CheckCircle className="h-5 w-5 text-cyan-500" /> {s.name}
                </h3>
                <p className="text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-slate-600">
            More on how we keep a business running during a repaint:{' '}
            <Link to="/blog/office-shop-repaints-without-shutting-doors" className="text-cyan-600 hover:text-cyan-700 font-medium">
              office and shop repaints without shutting your doors
            </Link>
            . Landlords:{' '}
            <Link to="/blog/landlords-guide-between-tenant-refreshes-kent" className="text-cyan-600 hover:text-cyan-700 font-medium">
              our guide to between-tenant refreshes
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">How a commercial job works</h2>
          <ol className="space-y-6">
            {HOW.map(([h, t], i) => (
              <li key={h} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-white font-semibold">{i + 1}</span>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{h}</h3>
                  <p className="text-slate-600">{t}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">What business and landlord customers say</h2>
            <p className="text-slate-600 mb-8">Reviews taken from Checkatrade.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((r) => (
                <figure key={r.id} className="rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-1 text-amber-400 mb-2" aria-label={`${r.score} out of 10`}>
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                    <span className="ml-2 text-sm text-slate-500">{r.score}/10</span>
                  </div>
                  <p className="font-semibold text-slate-900 mb-2">{r.title}</p>
                  <blockquote className="text-slate-600 mb-3">{r.review}</blockquote>
                  <figcaption className="text-sm text-slate-500">
                    {r.customerName}, {r.location}, {r.date}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Questions from business customers</h2>
          <div className="space-y-6">
            {FAQS.map((f) => (
              <div key={f.question}>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.question}</h3>
                <p className="text-slate-600">{f.answer}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-slate-600">
            Local pages:{' '}
            <Link to="/painters-decorators-ashford" className="text-cyan-600 hover:text-cyan-700 font-medium">Ashford</Link>,{' '}
            <Link to="/painters-decorators-canterbury" className="text-cyan-600 hover:text-cyan-700 font-medium">Canterbury</Link>,{' '}
            <Link to="/painters-decorators-maidstone" className="text-cyan-600 hover:text-cyan-700 font-medium">Maidstone</Link>.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Get a free quote for your premises</h2>
          <p className="text-cyan-100 mb-8">Tell us about the job and we'll arrange a visit and a clear written quote.</p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 rounded-full px-8">
              Request your free quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CommercialPainting;
